import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineDate,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
  RowActions,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type OrderLine = {
  id?: number
  description: string
  quantity: string
  unit_price: string
  tax_rate: string
  revenue_account: number | null
  product: number | null
  sort_order: number
  subtotal?: string
  tax_amount?: string
  line_total?: string
}

type SalesOrder = {
  id: number
  number: string
  partner: number
  partner_name?: string
  order_date: string
  expected_delivery_date: string | null
  delivery_address: string
  status: 'DRAFT' | 'CONFIRMED' | 'DELIVERED' | 'INVOICED' | 'CANCELLED'
  currency_code: string
  notes: string
  subtotal: string
  tax_total: string
  total: string
  lines: OrderLine[]
  invoice: number | null
  invoice_number: string | null
}

type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }
type Account = { id: number; code: string; name: string; account_type: string; is_active: boolean }
type Product = {
  id: number; sku: string; name: string; sales_price: string;
  unit_of_measure: string; is_active: boolean; product_type: string
}
type Warehouse = { id: number; code: string; name: string; is_active: boolean; is_default: boolean }

const STATUS_COLOR: Record<SalesOrder['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  DELIVERED: 'bg-purple-100 text-purple-800',
  INVOICED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-700',
}

type TabKey = 'details' | 'lines' | 'files'

// ─────────────────── Page ───────────────────
export default function SalesOrderDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const orderId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const orderQ = useQuery({
    queryKey: ['sales', 'order', orderId],
    queryFn: async () => (await api.get<SalesOrder>(`/sales/orders/${orderId}/`)).data,
    enabled: !!orderId,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })
  const accountsQ = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/finance/accounts/')).data,
  })
  const productsQ = useQuery({
    queryKey: ['inventory', 'products'],
    queryFn: async () => (await api.get<Product[]>('/inventory/products/')).data,
  })
  const warehousesQ = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: async () => (await api.get<Warehouse[]>('/inventory/warehouses/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<SalesOrder>) =>
      (await api.patch(`/sales/orders/${orderId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'order', orderId] })
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/sales/orders/${orderId}/`),
    onSuccess: () => {
      toast.success('Order deleted')
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
      navigate('/admin/sales/orders')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const convertMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/sales/orders/${orderId}/convert_to_invoice/`)).data,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['sales', 'order', orderId] })
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
      qc.invalidateQueries({ queryKey: ['finance', 'invoices'] })
      toast.success('Converted to invoice')
      if (data?.id) navigate(`/admin/finance/invoices/${data.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  // Deliver drawer state
  const [deliverOpen, setDeliverOpen] = useState(false)
  const [deliverWarehouse, setDeliverWarehouse] = useState<number | null>(null)

  const deliverMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/sales/orders/${orderId}/deliver/`, {
        warehouse_id: deliverWarehouse,
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'order', orderId] })
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
      qc.invalidateQueries({ queryKey: ['inventory'] })
      toast.success('Delivered (stock movements created)')
      setDeliverOpen(false)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (orderQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (orderQ.isError || !orderQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Order not found.</p>
          <Link to="/admin/sales/orders" className="text-blue-600 hover:underline">← Back to orders</Link>
        </div>
      </AdminLayout>
    )
  }

  const order = orderQ.data
  const partners = (partnersQ.data || []).filter(
    (p) => p.is_active && (p.partner_type === 'CUSTOMER' || p.partner_type === 'BOTH')
  )
  const revenueAccounts = (accountsQ.data || []).filter(
    (a) => a.is_active && a.account_type === 'REVENUE'
  )
  // Orders move physical product, so SERVICE products aren't valid here.
  const products = (productsQ.data || []).filter(
    (p) => p.is_active && p.product_type !== 'SERVICE'
  )
  const warehouses = (warehousesQ.data || []).filter((w) => w.is_active)

  const locked = order.invoice != null || order.status === 'CANCELLED'
  const canDeliver = order.status === 'CONFIRMED' && warehouses.length > 0
  const canConvert = order.status === 'DELIVERED' && !order.invoice

  const save = (payload: Partial<SalesOrder>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  function openDeliver() {
    const def = warehouses.find((w) => w.is_default) || warehouses[0]
    setDeliverWarehouse(def?.id ?? null)
    setDeliverOpen(true)
  }

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/sales/orders" className="text-muted-foreground hover:text-foreground hover:underline">← Orders</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{order.number}</div>
            <h2 className="text-2xl font-bold text-foreground">
              {order.partner_name || <span className="italic">No customer</span>}
            </h2>
            <div className="text-sm text-muted-foreground">
              <span>Ordered {order.order_date}</span>
              {order.expected_delivery_date && <span> · Delivery {order.expected_delivery_date}</span>}
              <span> · </span>
              <span className="font-mono">{order.currency_code} {Number(order.total).toFixed(2)}</span>
              {order.invoice_number && (
                <span> · Invoiced as <Link to={`/admin/finance/invoices/${order.invoice}`}
                  className="text-blue-600 hover:underline font-mono">{order.invoice_number}</Link></span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {locked ? (
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[order.status]}`}>{order.status}</span>
            ) : (
              <InlineStatus
                value={order.status}
                colorMap={STATUS_COLOR}
                options={[
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'CONFIRMED', label: 'Confirmed' },
                  { value: 'DELIVERED', label: 'Delivered' },
                  { value: 'INVOICED', label: 'Invoiced' },
                  { value: 'CANCELLED', label: 'Cancelled' },
                ]}
                onSave={(v) => save({ status: v })}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canDeliver && (
                  <DropdownMenuItem onClick={openDeliver} className="text-purple-700">
                    Deliver…
                  </DropdownMenuItem>
                )}
                {canConvert && (
                  <DropdownMenuItem
                    onClick={() => confirm(
                      `Convert order ${order.number} to a customer invoice`,
                      () => convertMut.mutate(),
                    )}
                    disabled={convertMut.isPending}
                    className="text-green-700"
                  >
                    Convert to invoice
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  disabled={!!order.invoice}
                  onClick={() => !order.invoice && confirm(
                    `order ${order.number}`,
                    () => deleteMut.mutate(),
                  )}
                  className={!order.invoice ? 'text-destructive' : ''}
                >
                  {order.invoice ? `Delete (invoiced as ${order.invoice_number})` : 'Delete order'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'lines', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'lines' && `Line items (${order.lines.length})`}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && (
        <DetailsTab order={order} partners={partners} locked={locked} onSave={save} />
      )}
      {tab === 'lines' && (
        <LinesTab
          order={order}
          revenueAccounts={revenueAccounts}
          products={products}
          locked={locked}
          onSaveLines={(newLines) => save({ lines: newLines } as Partial<SalesOrder>)}
        />
      )}
      {tab === 'files' && <FilesTab parent="sales.order" parentId={orderId} />}

      {/* Deliver drawer */}
      <RecordDrawer
        open={deliverOpen}
        onOpenChange={(v) => { if (!v) setDeliverOpen(false) }}
        title="Deliver order"
        description="Records the delivery and creates outbound stock movements from the chosen warehouse. Moves status to DELIVERED."
        onSubmit={() => { if (deliverWarehouse) deliverMut.mutate() }}
        isPending={deliverMut.isPending}
        submitLabel="Record delivery"
      >
        <div className="bg-muted/40 rounded p-3 mb-4 text-sm">
          <div className="font-medium">{order.partner_name}</div>
          <div className="text-xs text-muted-foreground mt-1">
            Order <span className="font-mono">{order.number}</span> · {order.lines.length} line{order.lines.length === 1 ? '' : 's'}
          </div>
        </div>

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="dv-wh">Ship from warehouse *</FieldLabel>
            <SearchableSelect
              id="dv-wh"
              value={deliverWarehouse ? String(deliverWarehouse) : undefined}
              onChange={(v) => setDeliverWarehouse(Number(v))}
              placeholder="— select —"
              options={warehouses.map((w) => ({
                value: String(w.id),
                label: `${w.name}${w.is_default ? ' (default)' : ''}`,
                hint: w.code,
              }))}
            />
            <FieldDescription>
              Stock will be deducted from this warehouse. Make sure on-hand quantities cover the order.
            </FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  order, partners, locked, onSave,
}: {
  order: SalesOrder
  partners: Partner[]
  locked: boolean
  onSave: (payload: Partial<SalesOrder>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">
      {order.invoice != null && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
          This order is <strong>invoiced</strong> ({order.invoice_number}) and read-only. Edit the invoice for billing changes.
        </div>
      )}
      {order.status === 'CANCELLED' && order.invoice == null && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
          This order is <strong>cancelled</strong>. Reactivate by changing status before editing.
        </div>
      )}

      <Section title="Order header">
        <Row label="Number">
          <InlineText
            value={order.number}
            onSave={(v) => onSave({ number: v })}
            disabled={locked}
            className="font-mono"
          />
        </Row>
        <Row label="Customer">
          <InlineSelect
            value={String(order.partner)}
            displayLabel={order.partner_name || <Dash />}
            options={partners.map((p) => ({
              value: String(p.id),
              label: `${p.code} ${p.name}`,
              searchText: `${p.code} ${p.name}`,
            }))}
            onSave={(v) => onSave({ partner: Number(v) })}
            disabled={locked}
          />
        </Row>
        <Row label="Order date">
          <InlineDate
            value={order.order_date}
            onSave={(v) => onSave({ order_date: v || order.order_date })}
          />
        </Row>
        <Row label="Expected delivery">
          <InlineDate
            value={order.expected_delivery_date}
            onSave={(v) => onSave({ expected_delivery_date: v })}
          />
        </Row>
        <Row label="Currency">
          <InlineText
            value={order.currency_code}
            onSave={(v) => onSave({ currency_code: v.toUpperCase().slice(0, 3) })}
            disabled={locked}
            className="font-mono"
          />
        </Row>
        <Row label="Status">
          {locked ? (
            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[order.status]}`}>{order.status}</span>
          ) : (
            <InlineStatus
              value={order.status}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'DRAFT', label: 'Draft' },
                { value: 'CONFIRMED', label: 'Confirmed' },
                { value: 'DELIVERED', label: 'Delivered' },
                { value: 'INVOICED', label: 'Invoiced' },
                { value: 'CANCELLED', label: 'Cancelled' },
              ]}
              onSave={(v) => onSave({ status: v })}
            />
          )}
        </Row>
      </Section>

      <Section title="Delivery">
        <Row label="Delivery address">
          <InlineText
            value={order.delivery_address}
            multiline
            onSave={(v) => onSave({ delivery_address: v })}
            disabled={locked}
            placeholder="Full delivery address"
          />
        </Row>
      </Section>

      <Section title="Totals">
        <Row label="Subtotal">
          <span className="font-mono">{Number(order.subtotal).toFixed(2)} {order.currency_code}</span>
        </Row>
        <Row label="Tax">
          <span className="font-mono">{Number(order.tax_total).toFixed(2)} {order.currency_code}</span>
        </Row>
        <Row label="Total">
          <span className="font-mono font-bold text-base">
            {Number(order.total).toFixed(2)} {order.currency_code}
          </span>
        </Row>
        {order.invoice_number && (
          <Row label="Invoice">
            <Link to={`/admin/finance/invoices/${order.invoice}`}
              className="text-blue-600 hover:underline font-mono text-sm">
              {order.invoice_number}
            </Link>
          </Row>
        )}
      </Section>

      <Section title="Notes">
        <Row label="Notes">
          <InlineText
            value={order.notes}
            multiline
            onSave={(v) => onSave({ notes: v })}
            disabled={locked}
            placeholder="Internal notes on this order"
          />
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Line items tab ───────────────────

type LineDraft = {
  description: string
  quantity: string
  unit_price: string
  tax_rate: string
  revenue_account: number | null
  product: number | null
}

function emptyLineDraft(revenueAccounts: Account[]): LineDraft {
  return {
    description: '',
    quantity: '1',
    unit_price: '0',
    tax_rate: '0',
    revenue_account: revenueAccounts[0]?.id ?? null,
    product: null,
  }
}

function LinesTab({
  order, revenueAccounts, products, locked, onSaveLines,
}: {
  order: SalesOrder
  revenueAccounts: Account[]
  products: Product[]
  locked: boolean
  onSaveLines: (lines: OrderLine[]) => Promise<void>
}) {
  const { confirm, dialog } = useDeleteConfirm()
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<LineDraft>(emptyLineDraft(revenueAccounts))
  const [pending, setPending] = useState(false)

  function openCreate() {
    setDraft(emptyLineDraft(revenueAccounts))
    setCreating(true)
  }

  async function saveLineField(idx: number, patch: Partial<OrderLine>) {
    const next = order.lines.map((l, i) => i === idx ? { ...l, ...patch } : l)
    await onSaveLines(serializeLines(next))
  }

  async function addLine() {
    setPending(true)
    try {
      const next: OrderLine[] = [
        ...order.lines,
        {
          description: draft.description,
          quantity: draft.quantity,
          unit_price: draft.unit_price,
          tax_rate: draft.tax_rate,
          revenue_account: draft.revenue_account,
          product: draft.product,
          sort_order: order.lines.length,
        },
      ]
      await onSaveLines(serializeLines(next))
      toast.success('Line added')
      setCreating(false)
    } catch (e) {
      toast.error(extractErr(e))
    } finally {
      setPending(false)
    }
  }

  async function removeLine(idx: number) {
    const next = order.lines.filter((_, i) => i !== idx)
    try {
      await onSaveLines(serializeLines(next))
      toast.success('Line removed')
    } catch (e) {
      toast.error(extractErr(e))
    }
  }

  function setDraftProduct(productId: number | null) {
    const p = products.find((x) => x.id === productId)
    if (p) {
      setDraft({
        ...draft,
        product: productId,
        description: draft.description || p.name,
        unit_price: draft.unit_price === '0' ? p.sales_price : draft.unit_price,
      })
    } else {
      setDraft({ ...draft, product: productId })
    }
  }

  let subtotal = 0, taxTotal = 0
  order.lines.forEach((l) => {
    const s = Number(l.quantity) * Number(l.unit_price)
    const t = s * Number(l.tax_rate) / 100
    subtotal += s; taxTotal += t
  })

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          {locked
            ? 'Order locked — line items are read-only.'
            : 'Edit quantities, unit prices, and tax rates inline. Service products are not allowed on sales orders.'}
        </p>
        <Button size="sm" onClick={openCreate} disabled={locked}>
          <Plus className="h-3 w-3 mr-1" /> Add line
        </Button>
      </div>

      {order.lines.length === 0 ? (
        <p className="text-muted-foreground text-sm">No line items yet.</p>
      ) : (
        <div className="bg-card rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>Description</Th>
                <Th>Product</Th>
                <Th className="text-right">Qty</Th>
                <Th className="text-right">Unit price</Th>
                <Th className="text-right">Tax %</Th>
                <Th>Revenue account</Th>
                <Th className="text-right">Line total</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {order.lines.map((l, i) => {
                const s = Number(l.quantity) * Number(l.unit_price)
                const t = s * Number(l.tax_rate) / 100
                const acct = revenueAccounts.find((a) => a.id === l.revenue_account)
                const prod = l.product ? products.find((p) => p.id === l.product) : null
                return (
                  <tr key={l.id ?? i}>
                    <Td className="font-medium">
                      <InlineText
                        value={l.description}
                        onSave={(v) => saveLineField(i, { description: v })}
                        disabled={locked}
                        placeholder="Item description"
                      />
                    </Td>
                    <Td className="text-xs">
                      <InlineSelect
                        value={l.product ? String(l.product) : ''}
                        displayLabel={prod ? <span className="font-mono">{prod.sku}</span> : <Dash />}
                        allowClear
                        options={products.map((p) => ({
                          value: String(p.id),
                          label: `${p.sku} — ${p.name}`,
                          searchText: `${p.sku} ${p.name}`,
                        }))}
                        onSave={(v) => saveLineField(i, { product: v ? Number(v) : null })}
                        disabled={locked}
                      />
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.quantity}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { quantity: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}</span>}
                      />
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.unit_price}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { unit_price: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}</span>}
                      />
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.tax_rate}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { tax_rate: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}%</span>}
                      />
                    </Td>
                    <Td className="text-xs">
                      <InlineSelect
                        value={l.revenue_account ? String(l.revenue_account) : ''}
                        displayLabel={acct ? `${acct.code} ${acct.name}` : <Dash />}
                        allowClear
                        options={revenueAccounts.map((a) => ({
                          value: String(a.id),
                          label: `${a.code} ${a.name}`,
                          searchText: `${a.code} ${a.name}`,
                        }))}
                        onSave={(v) => saveLineField(i, { revenue_account: v ? Number(v) : null })}
                        disabled={locked}
                      />
                    </Td>
                    <Td className="text-right font-mono text-xs">
                      {(s + t).toFixed(2)}
                    </Td>
                    <Td className="text-right">
                      {!locked && (
                        <RowActions
                          onEdit={() => {/* inline edits cover the whole row */}}
                          onDelete={() => confirm(
                            `line "${l.description || '(blank)'}"`,
                            () => removeLine(i),
                          )}
                        />
                      )}
                    </Td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot className="bg-muted/40 text-sm">
              <tr>
                <td colSpan={6} className="px-4 py-2 text-right font-medium text-muted-foreground">Subtotal</td>
                <td className="px-4 py-2 text-right font-mono">{subtotal.toFixed(2)}</td>
                <td />
              </tr>
              <tr>
                <td colSpan={6} className="px-4 py-2 text-right font-medium text-muted-foreground">Tax</td>
                <td className="px-4 py-2 text-right font-mono">{taxTotal.toFixed(2)}</td>
                <td />
              </tr>
              <tr className="font-bold">
                <td colSpan={6} className="px-4 py-2 text-right text-foreground">Total</td>
                <td className="px-4 py-2 text-right font-mono">{order.currency_code} {(subtotal + taxTotal).toFixed(2)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="Add line item"
        description="Add a product line. Picking a product pre-fills description and unit price."
        onSubmit={addLine}
        isPending={pending}
        submitLabel="Add line"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="li-prod">Product</FieldLabel>
            <SearchableSelect
              id="li-prod"
              value={draft.product ? String(draft.product) : NONE}
              onChange={(v) => setDraftProduct(v === NONE ? null : Number(v))}
              placeholder="— none —"
              options={[
                { value: NONE, label: '— none —' },
                ...products.map((p) => ({
                  value: String(p.id), label: p.name, hint: p.sku,
                })),
              ]}
            />
            <FieldDescription>Only physical products (no SERVICE types).</FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor="li-desc">Description *</FieldLabel>
            <Textarea id="li-desc" autoFocus required value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              rows={2} />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field>
              <FieldLabel htmlFor="li-qty">Quantity</FieldLabel>
              <Input id="li-qty" type="number" step="0.01" value={draft.quantity}
                onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="li-price">Unit price</FieldLabel>
              <Input id="li-price" type="number" step="0.01" value={draft.unit_price}
                onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })}
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="li-tax">Tax %</FieldLabel>
              <Input id="li-tax" type="number" step="0.01" value={draft.tax_rate}
                onChange={(e) => setDraft({ ...draft, tax_rate: e.target.value })}
                className="font-mono text-right" />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="li-acct">Revenue account</FieldLabel>
            <SearchableSelect
              id="li-acct"
              value={draft.revenue_account ? String(draft.revenue_account) : NONE}
              onChange={(v) => setDraft({ ...draft, revenue_account: v === NONE ? null : Number(v) })}
              placeholder="— none —"
              options={[
                { value: NONE, label: '— none —' },
                ...revenueAccounts.map((a) => ({
                  value: String(a.id), label: a.name, hint: a.code,
                })),
              ]}
            />
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

function serializeLines(lines: OrderLine[]): OrderLine[] {
  return lines.map((l, i) => ({
    description: l.description,
    quantity: l.quantity,
    unit_price: l.unit_price,
    tax_rate: l.tax_rate,
    revenue_account: l.revenue_account,
    product: l.product,
    sort_order: i,
  }))
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
